import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import {
  alerts,
  devices,
  type AlertKind,
  type AlertMetric,
  type AlertRule,
  type AlertRuleInput,
  type DeviceSummary,
} from "../lib/api.js";
import { relativeTime } from "../lib/freshness.js";
import { useLiveData } from "../lib/use-live-data.js";
import { useNow } from "../lib/use-now.js";

/**
 * Alerts: what fires, who hears about it, and what has happened.
 *
 * Preferences come first on the page deliberately. They are the part every
 * user has a reason to touch, and the part that decides whether any of the
 * rules below reach a human at all.
 */

const REFRESH_MS = 60_000;

const KIND_LABEL: Record<AlertKind, string> = {
  device_stale: "Device stopped reporting",
  poll_failure: "Poll failures",
  power_threshold: "Power thresholds",
  temperature_threshold: "Temperature thresholds",
  psu_failed: "Power supply failures",
};

const KIND_HELP: Record<AlertKind, string> = {
  device_stale: "No successful poll for several intervals — catches a site going dark",
  poll_failure: "Reachable but erroring, or unreachable outright",
  power_threshold: "Battery voltage, charge current or output power crossing a limit",
  temperature_threshold: "Board, heatsink or battery temperature crossing a limit",
  psu_failed: "A redundant power supply reporting failed, on routers that report PSU state",
};

const KINDS = Object.keys(KIND_LABEL) as AlertKind[];

export function AlertsPage() {
  return (
    <div style={{ display: "grid", gap: 28 }}>
      <PreferencesSection />
      <EventsSection />
      <RulesSection />
    </div>
  );
}

// ------------------------------------------------------------ preferences --

function PreferencesSection() {
  const [prefs, setPrefs] = useState<Record<AlertKind, boolean> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    alerts
      .preferences()
      .then((r) => {
        const map = {} as Record<AlertKind, boolean>;
        for (const kind of KINDS) map[kind] = false;
        for (const p of r.preferences) map[p.kind] = p.email;
        setPrefs(map);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Could not load preferences"));
  }, []);

  const save = async (next: Record<AlertKind, boolean>) => {
    setPrefs(next);
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await alerts.savePreferences(KINDS.map((kind) => ({ kind, email: next[kind] })));
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <h1 style={heading}>&gt; MY ALERT EMAILS</h1>
      <p style={help}>
        Only these categories are emailed to you, and only for devices you can
        already see. Nobody else can change these for you.
      </p>

      {error && <p style={{ color: "var(--status-critical)", fontSize: 13 }}>{`!! ${error}`}</p>}
      {!prefs && !error && <p style={{ color: "var(--text-muted)" }}>LOADING...</p>}

      {prefs && (
        <div style={{ ...card, display: "grid", gap: 10 }}>
          {KINDS.map((kind) => (
            <label key={kind} style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={prefs[kind]}
                onChange={(e) => void save({ ...prefs, [kind]: e.target.checked })}
                style={{ marginTop: 3 }}
              />
              <span>
                <span style={{ fontSize: 13 }}>{KIND_LABEL[kind]}</span>
                <span style={{ display: "block", color: "var(--text-muted)", fontSize: 11 }}>
                  {KIND_HELP[kind]}
                </span>
              </span>
            </label>
          ))}
          <div style={{ fontSize: 11, color: saved ? "var(--success-text)" : "var(--text-muted)" }}>
            {saving ? "SAVING..." : saved ? "SAVED" : ""}
          </div>
        </div>
      )}
    </section>
  );
}

// ----------------------------------------------------------------- events --

function EventsSection() {
  const now = useNow();
  const { data, error } = useLiveData(() => alerts.events(), REFRESH_MS);
  const events = data?.events ?? [];
  const open = events.filter((e) => e.state === "open");

  return (
    <section>
      <h1 style={heading}>&gt; ALERTS {open.length > 0 && `(${open.length} OPEN)`}</h1>

      {error && !data && <p style={{ color: "var(--status-critical)", fontSize: 13 }}>{`!! ${error}`}</p>}
      {data && events.length === 0 && (
        <p style={{ color: "var(--text-muted)", fontSize: 13 }}>-- NOTHING HAS FIRED --</p>
      )}

      {events.length > 0 && (
        <div style={{ border: "1px solid var(--border)", background: "var(--surface-1)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)" }}>
                {["STATE", "RULE", "DEVICE", "DETAIL", "SINCE"].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} style={{ borderBottom: "1px solid var(--gridline)" }}>
                  <td style={td}>
                    <span
                      style={{
                        color:
                          event.state === "resolved"
                            ? "var(--text-muted)"
                            : event.severity === "critical"
                              ? "var(--status-critical)"
                              : "var(--status-warning)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {event.state === "resolved" ? "[ OK   ]" : event.severity === "critical" ? "[ CRIT ]" : "[ WARN ]"}
                    </span>
                  </td>
                  <td style={td}>{event.rule_name}</td>
                  <td style={td}>
                    <Link to={`/devices/${event.device_id}`} style={{ color: "var(--text-primary)" }}>
                      {event.device_name}
                    </Link>
                  </td>
                  <td style={{ ...td, color: "var(--text-secondary)" }}>{event.detail}</td>
                  <td style={{ ...td, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                    {relativeTime(event.state === "open" ? event.opened_at : event.resolved_at, now)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ rules --

function RulesSection() {
  const { data, error, reload } = useLiveData(() => alerts.rules(), REFRESH_MS);
  const [editing, setEditing] = useState<AlertRule | "new" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canManage = data?.canManage ?? false;

  const remove = async (rule: AlertRule) => {
    if (!confirm(`Delete "${rule.name}"? Its alert history goes with it.`)) return;
    setActionError(null);
    try {
      await alerts.deleteRule(rule.id);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not delete");
    }
  };

  return (
    <section>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <h1 style={heading}>&gt; ALERT RULES</h1>
        {canManage && !editing && (
          <button onClick={() => setEditing("new")} style={smallButton}>[ + ADD RULE ]</button>
        )}
      </div>
      <p style={help}>
        What counts as a problem. Rules are shared by the organisation; who gets
        emailed is each person's own setting above.
      </p>

      {actionError && <p style={{ color: "var(--status-critical)", fontSize: 13 }}>{`!! ${actionError}`}</p>}
      {error && !data && <p style={{ color: "var(--status-critical)", fontSize: 13 }}>{`!! ${error}`}</p>}

      {editing && (
        <RuleForm
          rule={editing === "new" ? null : editing}
          onDone={() => { setEditing(null); reload(); }}
        />
      )}

      {data && data.rules.length === 0 && !editing && (
        <p style={{ color: "var(--text-muted)", fontSize: 13 }}>-- NO RULES YET --</p>
      )}

      {data && data.rules.length > 0 && (
        <div style={{ border: "1px solid var(--border)", background: "var(--surface-1)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)" }}>
                {["NAME", "WHEN", "APPLIES TO", "OPEN", ...(canManage ? [""] : [])].map((h, i) => (
                  <th key={h || `a${i}`} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rules.map((rule) => (
                <tr key={rule.id} style={{ borderBottom: "1px solid var(--gridline)", opacity: rule.enabled ? 1 : 0.5 }}>
                  <td style={td}>
                    {rule.name}
                    {!rule.enabled && <span style={{ color: "var(--text-muted)", fontSize: 11 }}> (disabled)</span>}
                  </td>
                  <td style={{ ...td, color: "var(--text-secondary)" }}>{describeRule(rule)}</td>
                  <td style={{ ...td, color: "var(--text-secondary)" }}>
                    {rule.device_name ?? rule.group_name ?? "All devices"}
                  </td>
                  <td style={td}>
                    {Number(rule.open_count) > 0 ? (
                      <span style={{ color: "var(--status-critical)" }}>{rule.open_count}</span>
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>0</span>
                    )}
                  </td>
                  {canManage && (
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button onClick={() => setEditing(rule)} style={smallButton}>[ EDIT ]</button>{" "}
                      <button onClick={() => void remove(rule)} style={{ ...smallButton, color: "var(--status-critical)" }}>
                        [ DELETE ]
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** A one-line, human reading of what the rule actually watches. */
function describeRule(rule: AlertRule): string {
  switch (rule.kind) {
    case "device_stale":
      return `No poll for ${rule.stale_multiplier}x its interval`;
    case "poll_failure":
      return "Polling is failing";
    default:
      return `${rule.metric ?? "?"} ${rule.comparison ?? "?"} ${rule.threshold ?? "?"}`;
  }
}

function RuleForm({ rule, onDone }: { rule: AlertRule | null; onDone: () => void }) {
  const [metrics, setMetrics] = useState<AlertMetric[]>([]);
  const [deviceList, setDeviceList] = useState<DeviceSummary[]>([]);
  const [form, setForm] = useState<AlertRuleInput>({
    name: rule?.name ?? "",
    kind: rule?.kind ?? "device_stale",
    severity: rule?.severity ?? "warning",
    enabled: rule?.enabled ?? true,
    scope_device_id: rule?.scope_device_id ?? null,
    metric: rule?.metric ?? null,
    comparison: rule?.comparison ?? null,
    threshold: rule?.threshold ?? null,
    trigger_after: rule?.trigger_after ?? 2,
    resolve_after: rule?.resolve_after ?? 2,
    renotify_minutes: rule?.renotify_minutes ?? null,
    stale_multiplier: rule?.stale_multiplier ?? 3,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    alerts.metrics().then((r) => setMetrics(r.metrics)).catch(() => setMetrics([]));
    devices.list().then((r) => setDeviceList(r.devices)).catch(() => setDeviceList([]));
  }, []);

  const isThreshold = form.kind === "power_threshold" || form.kind === "temperature_threshold";
  const relevant = metrics.filter((m) => m.kind === form.kind);

  const set = <K extends keyof AlertRuleInput>(key: K, value: AlertRuleInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (rule) await alerts.updateRule(rule.id, form);
      else await alerts.createRule(form);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save rule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ ...card, marginBottom: 14, display: "grid", gap: 12 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div>
          <label style={label}>&gt; NAME</label>
          <input required value={form.name} onChange={(e) => set("name", e.target.value)} style={{ ...input, width: 240 }} />
        </div>
        <div>
          <label style={label}>&gt; WHEN</label>
          <select
            value={form.kind}
            onChange={(e) => {
              const kind = e.target.value as AlertKind;
              // Switching kind clears the measurement, since the old one
              // usually belongs to the other category.
              set("kind", kind);
              set("metric", null);
              set("comparison", null);
            }}
            style={select}
          >
            {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k].toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>&gt; SEVERITY</label>
          <select value={form.severity} onChange={(e) => set("severity", e.target.value as "warning" | "critical")} style={select}>
            <option value="warning">WARNING</option>
            <option value="critical">CRITICAL</option>
          </select>
        </div>
      </div>

      {isThreshold && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={label}>&gt; MEASUREMENT</label>
            <select
              value={form.metric ?? ""}
              onChange={(e) => {
                const metric = relevant.find((m) => m.id === e.target.value);
                set("metric", e.target.value || null);
                // Preselect the direction the measurement is usually wrong in.
                if (metric && !form.comparison) set("comparison", metric.typical);
              }}
              style={select}
            >
              <option value="">-- CHOOSE --</option>
              {relevant.map((m) => <option key={m.id} value={m.id}>{m.label.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>&gt; GOES</label>
            <select value={form.comparison ?? ""} onChange={(e) => set("comparison", (e.target.value || null) as "above" | "below" | null)} style={select}>
              <option value="">--</option>
              <option value="below">BELOW</option>
              <option value="above">ABOVE</option>
            </select>
          </div>
          <div>
            <label style={label}>&gt; THRESHOLD</label>
            <input
              type="number"
              step="any"
              value={form.threshold ?? ""}
              onChange={(e) => set("threshold", e.target.value === "" ? null : Number(e.target.value))}
              style={{ ...input, width: 110 }}
            />
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label style={label}>&gt; APPLIES TO</label>
          <select
            value={form.scope_device_id ?? ""}
            onChange={(e) => set("scope_device_id", e.target.value || null)}
            style={select}
          >
            <option value="">ALL DEVICES</option>
            {deviceList.map((d) => <option key={d.id} value={d.id}>{d.name.toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>&gt; FIRE AFTER</label>
          <input
            type="number" min={1} max={60}
            value={form.trigger_after}
            onChange={(e) => set("trigger_after", Number(e.target.value))}
            style={{ ...input, width: 70 }}
          />
        </div>
        <div>
          <label style={label}>&gt; CLEAR AFTER</label>
          <input
            type="number" min={1} max={60}
            value={form.resolve_after}
            onChange={(e) => set("resolve_after", Number(e.target.value))}
            style={{ ...input, width: 70 }}
          />
        </div>
        <div>
          <label style={label}>&gt; REMIND EVERY</label>
          <input
            type="number" min={5} placeholder="never"
            value={form.renotify_minutes ?? ""}
            onChange={(e) => set("renotify_minutes", e.target.value === "" ? null : Number(e.target.value))}
            style={{ ...input, width: 90 }}
          />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
          ENABLED
        </label>
      </div>

      <p style={{ ...help, margin: 0 }}>
        FIRE AFTER and CLEAR AFTER are consecutive observations, one per poll.
        Both above 1 is what stops a device sitting on the threshold emailing
        every minute. REMIND EVERY is in minutes; blank means one email per
        incident.
      </p>

      {error && <p style={{ color: "var(--status-critical)", fontSize: 13, margin: 0 }}>{`!! ${error}`}</p>}

      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={saving} style={{ ...smallButton, padding: "8px 14px" }}>
          {saving ? "[ SAVING... ]" : rule ? "[ SAVE ]" : "[ CREATE RULE ]"}
        </button>
        <button type="button" onClick={onDone} style={{ ...smallButton, padding: "8px 14px" }}>[ CANCEL ]</button>
      </div>
    </form>
  );
}

const heading: React.CSSProperties = { fontSize: 15, margin: "0 0 6px", letterSpacing: "0.14em" };
const help: React.CSSProperties = { color: "var(--text-muted)", fontSize: 11, margin: "0 0 12px", lineHeight: 1.5 };
const card: React.CSSProperties = { border: "1px solid var(--border)", background: "var(--surface-1)", padding: 14 };
const th: React.CSSProperties = { padding: "10px 14px", color: "var(--text-muted)", fontWeight: 500 };
const td: React.CSSProperties = { padding: "10px 14px" };
const input: React.CSSProperties = {
  padding: "7px 9px", fontSize: 13, border: "1px solid var(--border)",
  background: "var(--surface-raised)", color: "var(--text-primary)",
};
const select: React.CSSProperties = {
  padding: "6px 8px", fontSize: 12, border: "1px solid var(--border)",
  background: "var(--surface-raised)", color: "var(--text-primary)", letterSpacing: "0.06em",
};
const label: React.CSSProperties = {
  display: "block", fontSize: 11, color: "var(--text-secondary)", marginBottom: 4, letterSpacing: "0.08em",
};
const smallButton: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "5px 9px",
  fontSize: 11, color: "var(--text-secondary)", cursor: "pointer", letterSpacing: "0.08em",
};
